import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { AssignOwnerDialog } from '../features/assignments/AssignOwnerDialog';
import { useSession } from '../lib/session';
import type { AccountSummary } from '../lib/types';
import {
  CADENCE_LABEL,
  CADENCE_TONE,
  HEALTH_LABEL,
  HEALTH_TONE,
  IMPORTANCE_TONE,
  formatDate,
  relativeDays,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Button,
  Callout,
  Card,
  Detail,
  Dialog,
  ErrorNotice,
  Field,
  Loading,
  PageHeader,
  Select,
  StatTile,
  Tabs,
  Textarea,
  useToast,
  type TabDefinition,
} from '../components/ui';
import { CreateWorkItemDialog } from '../features/CreateWorkItemDialog';
import { AccountOverviewTab } from '../features/account-tabs/OverviewTab';
import { AccountSuccessPlanTab } from '../features/account-tabs/SuccessPlanTab';
import { AccountProjectsTab } from '../features/account-tabs/ProjectsTab';
import { AccountWorkTab } from '../features/account-tabs/WorkTab';
import { AccountContactsTab } from '../features/account-tabs/ContactsTab';
import { AccountActivityTab } from '../features/account-tabs/ActivityTab';

/**
 * §52 Account screen.
 *
 * The header answers the questions a CSM opens an account to answer: who owns it,
 * how important it is, when we last spoke, when we speak next, and whether we are
 * keeping to the rhythm we agreed. Everything below is the detail behind that.
 */
export function AccountDetailPage() {
  const { accountId = '' } = useParams();
  const { me } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'overview';
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [editingHealth, setEditingHealth] = useState(false);
  const [editingImportance, setEditingImportance] = useState(false);

  const summary = useQuery({
    queryKey: ['account-summary', accountId],
    queryFn: () => api.get<AccountSummary>(`/accounts/${accountId}/summary`),
  });

  const data = summary.data;
  const account = data?.account;

  const tabs: TabDefinition[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'success-plan', label: 'Success Plan' },
    { id: 'projects', label: 'Projects', count: data?.openWork.openProjects },
    { id: 'commitments', label: 'Commitments', count: data?.openWork.openCommitments },
    { id: 'followups', label: 'Follow-ups', count: data?.openWork.openFollowups },
    { id: 'contacts', label: 'Contacts' },
    { id: 'activity', label: 'Activity' },
    { id: 'meetings', label: 'Meetings', unavailable: 'Calendar and Fireflies arrive in Phase 4–5' },
    {
      id: 'product-requests',
      label: 'Product Requests',
      unavailable: 'The Product workspace arrives in Phase 7',
    },
  ];

  return (
    <>
      {summary.isLoading && <Loading label="Loading account" />}
      {summary.error && <ErrorNotice error={summary.error} onRetry={() => summary.refetch()} />}

      {data && account && (
        <>
          <PageHeader
            title={
              <span className="flex flex-wrap items-center gap-2">
                {account.name}
                <Badge
                  tone={IMPORTANCE_TONE[account.importance]}
                  dot={account.importance === 'strategic'}
                >
                  {titleCase(account.importance)}
                </Badge>
                <Badge tone={HEALTH_TONE[account.healthCurrent]}>
                  {HEALTH_LABEL[account.healthCurrent]}
                </Badge>
                <Badge tone={CADENCE_TONE[data.cadence.status]}>
                  {CADENCE_LABEL[data.cadence.status]}
                </Badge>
              </span>
            }
            description={
              <span>
                <Link
                  to={`/enterprises/${account.enterpriseId}`}
                  className="text-accent hover:underline"
                >
                  {account.enterpriseName}
                </Link>
                {account.accountCode && <span className="text-muted"> · {account.accountCode}</span>}
                <span className="text-muted">
                  {' '}
                  · {titleCase(account.status)} · {titleCase(account.lifecycle)} ·{' '}
                  {account.timezone}
                </span>
              </span>
            }
            actions={
              <>
                {me.capabilities.canOverrideAccountHealth && (
                  <Button size="sm" onClick={() => setEditingHealth(true)}>
                    Set health
                  </Button>
                )}
                {me.capabilities.canEditAccountImportance && (
                  <Button size="sm" onClick={() => setEditingImportance(true)}>
                    Set importance
                  </Button>
                )}
                {me.capabilities.canManageAssignments && (
                  <Button size="sm" onClick={() => setAssigning(true)}>
                    Assign owner
                  </Button>
                )}
                {me.capabilities.canCreateWorkItems && (
                  <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                    Record work
                  </Button>
                )}
              </>
            }
            meta={
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-border bg-surface px-4 py-3 sm:grid-cols-3 lg:grid-cols-6">
                <Detail label="Primary CSM">{account.primaryCsmName}</Detail>
                <Detail label="Secondary CSM">
                  {data.assignments.find((a) => a.assignmentType === 'secondary_csm' && a.isActive)
                    ?.fullName ?? '—'}
                </Detail>
                <Detail label="Executive sponsor">
                  {data.assignments.find(
                    (a) => a.assignmentType === 'executive_sponsor' && a.isActive,
                  )?.fullName ?? '—'}
                </Detail>
                <Detail label="Last customer contact">
                  {formatDate(data.interaction.lastCustomerContactAt)}
                  <span className="mt-0.5 block text-xs text-muted">
                    {data.interaction.lastCustomerContactAt
                      ? relativeDays(data.interaction.lastCustomerContactAt)
                      : 'none recorded'}
                  </span>
                </Detail>
                <Detail label="Last meeting">
                  {formatDate(data.interaction.lastCustomerMeetingAt)}
                  <span className="mt-0.5 block text-xs text-muted">
                    {data.interaction.lastCustomerMeetingAt
                      ? relativeDays(data.interaction.lastCustomerMeetingAt)
                      : 'none recorded'}
                  </span>
                </Detail>
                <Detail label="Next meeting">
                  {formatDate(data.interaction.nextScheduledMeetingAt)}
                  <span className="mt-0.5 block text-xs text-muted">
                    {data.interaction.nextScheduledMeetingAt
                      ? relativeDays(data.interaction.nextScheduledMeetingAt)
                      : 'nothing scheduled'}
                  </span>
                </Detail>
              </div>
            }
          />

          {/* Temporary coverage is worth stating plainly on the account itself. */}
          {data.assignments.some((a) => a.assignmentType === 'temporary_coverage' && a.isActive) && (
            <div className="mb-4">
              <Callout tone="info" title="Temporary coverage is active">
                {data.assignments
                  .filter((a) => a.assignmentType === 'temporary_coverage' && a.isActive)
                  .map((a) => (
                    <p key={a.id}>
                      {a.fullName} holds operational access until {formatDate(a.endsAt)}.
                      {a.notes && ` ${a.notes}`}
                    </p>
                  ))}
              </Callout>
            </div>
          )}

          {data.cadence.gaps.length > 0 && (
            <div className="mb-4">
              <Callout
                tone={data.cadence.status === 'behind' ? 'critical' : 'warning'}
                title={`Engagement gap — ${CADENCE_LABEL[data.cadence.status]}`}
              >
                <ul className="list-disc space-y-0.5 pl-4">
                  {data.cadence.gaps.map((gap) => (
                    <li key={gap.kind}>{gap.summary}</li>
                  ))}
                </ul>
                <p className="mt-1.5 text-muted">
                  An engagement gap is a fact about our contact rhythm, not a judgement about the
                  account.
                </p>
              </Callout>
            </div>
          )}

          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatTile
              label="Overdue commitments"
              value={data.openWork.overdueCommitments}
              tone="critical"
            />
            <StatTile label="At risk" value={data.openWork.atRiskCommitments} tone="serious" />
            <StatTile label="Open commitments" value={data.openWork.openCommitments} />
            <StatTile
              label="Overdue follow-ups"
              value={data.openWork.overdueFollowups}
              tone="serious"
            />
            <StatTile label="Open projects" value={data.openWork.openProjects} />
            <StatTile
              label="Customers to notify"
              value={data.openWork.pendingNotifications}
              tone="info"
            />
          </div>

          <Tabs
            tabs={tabs}
            active={activeTab}
            onChange={(id) => setSearchParams({ tab: id }, { replace: true })}
          />

          <div className="pt-4">
            {activeTab === 'overview' && <AccountOverviewTab summary={data} />}
            {activeTab === 'success-plan' && <AccountSuccessPlanTab accountId={accountId} />}
            {activeTab === 'projects' && (
              <AccountProjectsTab accountId={accountId} accountName={account.name} />
            )}
            {activeTab === 'commitments' && (
              <AccountWorkTab
                accountId={accountId}
                accountName={account.name}
                type="customer_commitment"
              />
            )}
            {activeTab === 'followups' && (
              <AccountWorkTab
                accountId={accountId}
                accountName={account.name}
                type="customer_followup"
              />
            )}
            {activeTab === 'contacts' && <AccountContactsTab accountId={accountId} />}
            {activeTab === 'activity' && <AccountActivityTab accountId={accountId} />}
          </div>

          {assigning && (
        <AssignOwnerDialog accountId={accountId!} onClose={() => setAssigning(false)} />
      )}
      {creating && (
            <CreateWorkItemDialog
              accountId={accountId}
              accountName={account.name}
              onClose={() => setCreating(false)}
            />
          )}
          {editingHealth && (
            <SetHealthDialog
              accountId={accountId}
              current={account.healthCurrent}
              onClose={() => setEditingHealth(false)}
            />
          )}
          {editingImportance && (
            <SetImportanceDialog
              accountId={accountId}
              current={account.importance}
              onClose={() => setEditingImportance(false)}
            />
          )}
        </>
      )}
    </>
  );
}

/** §23 — a health rating is a human judgement, and always carries its reason. */
function SetHealthDialog({
  accountId,
  current,
  onClose,
}: {
  accountId: string;
  current: string;
  onClose: () => void;
}) {
  const [health, setHealth] = useState(current);
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const toast = useToast();

  const evidence = useQuery({
    queryKey: ['account-health', accountId],
    queryFn: () => api.get<{ dimensions: Array<{ dimension: string; evidence: Record<string, unknown> }>; note: string }>(
      `/accounts/${accountId}/health`,
    ),
  });

  const mutation = useMutation({
    mutationFn: () => api.patch(`/accounts/${accountId}/health`, { health, reason }),
    onSuccess: () => {
      toast('Account health updated');
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Set account health"
      description="Your rating and your reason are both retained."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!reason.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Health" required>
          <Select value={health} onChange={(event) => setHealth(event.target.value)}>
            <option value="green">Green</option>
            <option value="yellow">Yellow</option>
            <option value="red">Red</option>
            <option value="unknown">Not rated</option>
          </Select>
        </Field>
        <Field
          label="Reason"
          required
          hint="Why this rating. Recorded on the timeline and in the audit trail."
        >
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
        </Field>

        {evidence.data && (
          <div className="rounded-md border border-border bg-surface-2/50 p-3">
            <p className="mb-2 text-xs font-semibold text-ink">Evidence</p>
            <dl className="space-y-2">
              {evidence.data.dimensions.map((dimension) => (
                <div key={dimension.dimension}>
                  <dt className="text-xs font-medium text-ink-2">
                    {titleCase(dimension.dimension)}
                  </dt>
                  <dd className="mt-0.5 font-mono text-[11px] break-words text-muted">
                    {JSON.stringify(dimension.evidence)}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-2 text-[11px] text-muted">{evidence.data.note}</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** §6.2 — importance is Leadership's call and never derived from contract value. */
function SetImportanceDialog({
  accountId,
  current,
  onClose,
}: {
  accountId: string;
  current: string;
  onClose: () => void;
}) {
  const [importance, setImportance] = useState(current);
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: () => api.patch(`/accounts/${accountId}/importance`, { importance, reason }),
    onSuccess: () => {
      toast('Account importance updated');
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Set account importance"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!reason.trim() || importance === current || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Callout tone="info" title="Importance is independent of contract value">
          A low-value account can still be strategic. Importance drives escalation thresholds — a
          strategic account escalates after 2 business days overdue, a standard one after 5.
        </Callout>
        <Field label="Importance" required>
          <Select value={importance} onChange={(event) => setImportance(event.target.value)}>
            <option value="strategic">Strategic</option>
            <option value="high">High</option>
            <option value="standard">Standard</option>
            <option value="project">Project</option>
          </Select>
        </Field>
        <Field label="Reason" required hint="Recorded on the timeline and in the audit trail.">
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
        </Field>
      </div>
    </Dialog>
  );
}
